import { useMemo } from 'react';
import { useDrawBudgetQuery } from '../../queries/drawQueries';

interface DrawLabelValue {
    label: string;
    value: number;
}

export const useDeriveDrawBudgetDataFromDrawBudgetId = (drawBudgetId: number) => {
    const { data: drawBudget, isLoading } = useDrawBudgetQuery(drawBudgetId);

    return useMemo(() => {
        if (!drawBudget) {
            return {
                drawBudget: undefined,
                latestDraw: undefined,
                drawLines: [] as DrawLabelValue[],
                total: 0,
                availableLines: [] as DrawLabelValue[],
                totalAvailable: 0,
                street: '',
                city: '',
                isLoading: true,
            };
        }

        const latestDraw = drawBudget.draws[drawBudget.draws?.length - 1];

        const drawLines: DrawLabelValue[] = [];
        const availableLines: DrawLabelValue[] = [];
        drawBudget.drawBudgetItems.forEach((budgetItem) => {
            if (!budgetItem.drawItems) return;

            let availableAmount = budgetItem.budgetAmount - budgetItem.equityAmount - budgetItem.acquisitionAmount;
            budgetItem.drawItems.forEach((drawItem) => {
                if (drawItem.funded > 0) availableAmount -= drawItem.funded;
            });
            if (availableAmount != 0) availableLines.push({ label: budgetItem.description, value: availableAmount });

            const drawItem = budgetItem.drawItems.find((di) => di.drawNumber == drawBudget.drawCount - 1);

            let value;
            if (latestDraw?.borrowerSubmitted || latestDraw?.needsBorrowerSignature) {
                value = drawItem?.funded ?? 0;
            } else {
                value = drawItem?.borrowerRequest ?? 0;
            }

            if (drawItem) drawLines.push({ label: budgetItem.description, value });
        });

        const total = drawLines.reduce((sum, line) => sum + line.value, 0);
        const totalAvailable = availableLines.reduce((sum, line) => sum + line.value, 0);

        const [address, cityPart, statePart] = drawBudget.propertyAddress?.split(',') ?? [];

        return {
            drawBudget,
            latestDraw,
            drawLines,
            total,
            availableLines,
            totalAvailable,
            street: address ?? '',
            city: `${cityPart ?? ''} ${statePart ?? ''}`.trim(),
            isLoading,
        };
    }, [drawBudget]);
};
